import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronLeft, Search } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { TerminalLoader } from "@/components/TerminalLoader";
import TradingViewWidget from "@/components/TradingViewWidget";
import { WatchlistRow } from "@/components/WatchlistRow";
import { TradePanel } from "@/components/TradePanel";
import { PositionsPanel } from "@/components/PositionsPanel";
import { TrendAnalysis, preloadTrendWidget } from "@/components/TrendAnalysis";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTheme } from "@/context/ThemeContext";
import { marketService, tradingService } from "@/services/marketService";
import { errorMessage } from "@/services/apiClient";
import { formatCurrency, formatPercent, formatPrice } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";

const INTERVALS = [
  { label: "1M", value: "1" },
  { label: "3M", value: "3" },
  { label: "5M", value: "5" },
  { label: "15M", value: "15" },
  { label: "30M", value: "30" },
  { label: "1H", value: "60" },
  { label: "4H", value: "240" },
  { label: "1D", value: "D" },
  { label: "1S", value: "W" },
];

const money = (v) => formatCurrency(v);

export default function TerminalPage() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const mobileView = params.get("view") || "chart";
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [catOpen, setCatOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [interval, setIntervalValue] = useState("60");
  const [summary, setSummary] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ready, setReady] = useState(false);
  const [trendOpen, setTrendOpen] = useState(false);
  const [noTrend, setNoTrend] = useState(() => new Set());
  const selectedRef = useRef(null);

  useEffect(() => {
    marketService.categories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const loadAssets = useCallback(async () => {
    try {
      const data = await marketService.assets({ category, search });
      setAssets(data);
      if (!selectedRef.current && data.length) {
        selectedRef.current = data[0];
        setSelected(data[0]);
      }
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [category, search]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await tradingService.summary());
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Resumo da conta indisponível (sessão a expirar?)", e);
      }
    }
  }, []);

  useEffect(() => {
    loadAssets();
    loadSummary();
    const t = setInterval(() => {
      loadAssets();
      loadSummary();
    }, 4000);
    return () => clearInterval(t);
  }, [loadAssets, loadSummary]);

  useEffect(() => {
    if (ready || !assets.length || !summary) return;
    const t = setTimeout(() => setReady(true), 1200);
    return () => clearTimeout(t);
  }, [assets.length, summary, ready]);

  const selectAsset = (a) => {
    selectedRef.current = a;
    setSelected(a);
    if (typeof window !== "undefined" && window.innerWidth < 768) setTradeOpen(true);
  };

  const toggleFavorite = async (a) => {
    try {
      await marketService.toggleFavorite(a.id);
      loadAssets();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const liveSelected = useMemo(
    () => assets.find((a) => a.id === selected?.id) || selected,
    [assets, selected]
  );

  const categoryLabel = categories.find((c) => c.slug === category)?.name || t("terminal.all");

  const { theme } = useTheme() || { theme: "dark" };
  const isLight = theme === "light";

  const markNoTrend = useCallback((tvSymbol) => {
    setNoTrend((prev) => new Set(prev).add(tvSymbol));
  }, []);

  // Pré-carrega o componente de análise assim que o terminal abre: ao clicar em "IA",
  // o script já está em cache e o painel aparece de imediato.
  useEffect(() => { preloadTrendWidget().catch(() => {}); }, []);

  const trendAvailable = Boolean(liveSelected?.tv_symbol) && !noTrend.has(liveSelected?.tv_symbol);

  const metrics = [
    { label: t("metrics.equity"), value: money(summary?.equity), testId: "metric-equity" },
    { label: t("metrics.freeMargin"), value: money(summary?.free_margin), testId: "metric-free-margin" },
    { label: t("metrics.usedMargin"), value: money(summary?.used_margin), testId: "metric-used-margin" },
    {
      label: t("metrics.openPnl"),
      value: money(summary?.open_pnl),
      testId: "metric-open-pnl",
      tone: (summary?.open_pnl || 0) >= 0 ? "text-[#10B981]" : "text-[#EF4444]",
    },
    { label: t("metrics.balance"), value: money(summary?.balance), testId: "metric-balance" },
    {
      label: t("metrics.marginLevel"),
      value: summary?.margin_level ? formatPercent(summary.margin_level) : "—",
      testId: "metric-margin-level",
    },
    { label: t("metrics.credit"), value: money(summary?.credit), testId: "metric-credit" },
  ];

  return (
    <TerminalShell account={summary} metrics={metrics} mobileSymbol={liveSelected?.symbol}>
      <TerminalLoader visible={!ready} />
      <div data-testid="terminal-page" className="flex flex-col gap-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[380px_1fr]">
          <section className={`${mobileView === "assets" ? "flex" : "hidden"} flex-col rounded-md border border-[#141A26] bg-[#0A0E16] md:flex`}>            <div className="flex items-center gap-3 p-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A5567]" />
                <input
                  data-testid="market-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("terminal.search")}
                  className="h-11 w-full rounded-md border border-[#1D2637] bg-[#0E1523] pl-10 pr-3 text-sm text-white outline-none transition-colors placeholder:text-[#4A5567] focus:border-[#2563EB]"
                />
              </div>
              <div className="relative">
                <button
                  onClick={() => setCatOpen(!catOpen)}
                  data-testid="category-dropdown-button"
                  className="flex h-11 min-w-[130px] items-center justify-between gap-2 rounded-md border border-[#2563EB]/60 bg-[#0E1523] px-3 text-sm text-white transition-colors hover:border-[#2563EB]"
                >
                  {categoryLabel}
                  <ChevronDown className={`h-3 w-3 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                </button>
                {catOpen ? (
                  <div
                    data-testid="category-dropdown-list"
                    className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-md border border-[#1D2637] bg-[#0E1523] py-2 shadow-2xl"
                  >
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        data-testid={`category-option-${c.slug}`}
                        onClick={() => {
                          setCategory(c.slug);
                          setCatOpen(false);
                        }}
                        className={`block w-full px-4 py-2 text-left text-sm transition-colors ${
                          category === c.slug ? "bg-[#132038] text-white" : "text-[#C7D0DE] hover:bg-[#141A26]"
                        }`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-between border-y border-[#141A26] bg-[#0D1420] px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#8A97AD]">
              <span data-testid="market-count">{t("terminal.market")} ({assets.length})</span>
              <span className="flex gap-10">
                <span>{t("terminal.sell")}</span>
                <span>{t("terminal.buy")}</span>
              </span>
            </div>

            <div className="h-[calc(100vh-300px)] min-h-[320px] overflow-y-auto md:h-auto md:max-h-[300px] md:min-h-[200px]" data-testid="watchlist">
              {assets.map((a) => (
                <WatchlistRow
                  key={a.id}
                  asset={a}
                  selected={a.id === selected?.id}
                  onSelect={selectAsset}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
              {assets.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-[#5C6B82]">Nenhum ativo encontrado</p>
              ) : null}
            </div>
          </section>

          <section className={`${mobileView === "chart" ? "flex" : "hidden"} flex-col rounded-md border border-[#141A26] bg-[#0A0E16] md:flex`}>
            <div className="flex flex-wrap items-center gap-3 border-b border-[#141A26] p-4">
              <button
                onClick={() => setTradeOpen(true)}
                disabled={!liveSelected}
                data-testid="open-trade-button"
                className="rounded-md bg-[#2563EB] px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50"
              >
                Trade
              </button>              <div className="flex items-center gap-1 rounded-md border border-[#1D2637] p-1">
                {INTERVALS.map((i) => (
                  <button
                    key={i.value}
                    onClick={() => setIntervalValue(i.value)}
                    data-testid={`interval-${i.label}`}
                    className={`rounded px-3 py-1.5 font-mono text-xs transition-colors ${
                      interval === i.value ? "bg-[#2563EB] text-white" : "text-[#8A97AD] hover:text-white"
                    }`}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-4 font-mono text-xs text-[#8A97AD]">
                <span data-testid="chart-symbol" className="text-white">{liveSelected?.symbol || "—"}</span>
                <span>Sell <span className="text-[#EF4444]">{formatPrice(liveSelected?.sell)}</span></span>
                <span>Buy <span className="text-[#10B981]">{formatPrice(liveSelected?.buy)}</span></span>
              </div>
            </div>

            <div className="relative h-[667px] w-full sm:h-[340px]">
              {liveSelected?.tv_symbol ? (
                <TradingViewWidget symbol={liveSelected.tv_symbol} interval={interval} />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#5C6B82]">
                  {t("terminal.selectAsset")}
                </div>
              )}

              {trendAvailable && !trendOpen ? (
                <button
                  data-testid="trend-analysis-toggle"
                  onClick={() => setTrendOpen(true)}
                  title="IA Tendência"
                  className={`absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-l-md border border-r-0 px-1.5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                    isLight
                      ? "border-[#D8DEE9] bg-white/95 text-[#2563EB] hover:border-[#2563EB] hover:text-[#1D4ED8]"
                      : "border-[#1D2637] bg-[#0A0E16]/95 text-[#60A5FA] hover:border-[#2563EB] hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-1 [writing-mode:vertical-rl]">
                    <ChevronLeft className="h-3 w-3 rotate-90" /> IA
                  </span>
                </button>
              ) : null}

              {trendAvailable && trendOpen ? (
                <Dialog open={trendOpen} onOpenChange={setTrendOpen}>
                  <DialogContent
                    data-testid="trend-analysis-panel"
                    className={`max-w-[480px] gap-0 p-0 ${
                      isLight
                        ? "border-[#D8DEE9] bg-white text-[#0F172A]"
                        : "border-[#1D2637] bg-[#0A0E16] text-white"
                    }`}
                  >
                    <DialogHeader className={`border-b px-4 py-3 ${
                      isLight ? "border-[#E7EBF2]" : "border-[#141A26]"}`}>
                      <DialogTitle className={`flex items-center gap-2 font-mono text-xs font-normal tracking-[0.1em] ${
                        isLight ? "text-[#0F172A]" : "text-white"}`}>
                        <span className={`font-semibold uppercase tracking-[0.18em] ${
                          isLight ? "text-[#2563EB]" : "text-[#60A5FA]"}`}>
                          IA Tendência
                        </span>
                        <span data-testid="trend-analysis-context"
                              className={isLight ? "text-[#64748B]" : "text-[#8A97AD]"}>
                          {liveSelected.symbol} · {INTERVALS.find((i) => i.value === interval)?.label || interval}
                        </span>
                      </DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[70vh] overflow-y-auto">
                      <TrendAnalysis tvSymbol={liveSelected.tv_symbol} interval={interval}
                                     onUnavailable={markNoTrend} />
                    </div>
                  </DialogContent>
                </Dialog>
              ) : null}
            </div>
          </section>
        </div>

        <div className={`${mobileView === "history" ? "block" : "hidden"} md:block`}>
          <PositionsPanel
            refreshKey={refreshKey}
            onChanged={() => {
              setRefreshKey((k) => k + 1);
              loadSummary();
            }}
          />
        </div>
      </div>

      {liveSelected ? (
        <TradePanel
          asset={liveSelected}
          open={tradeOpen}
          onOpenChange={setTradeOpen}
          freeMargin={summary?.free_margin}
          leverage={summary?.leverage || 30}
          onDone={() => {
            setRefreshKey((k) => k + 1);
            loadSummary();
          }}
        />
      ) : null}
    </TerminalShell>
  );
}
